import i18n from 'i18next';

export type PosFormValues = { [key: string]: string | boolean | number };

/** "This field only applies while another field of the same form holds one of these values." */
export type PosFieldCondition = { field: string; oneOf: string[] };

type PosFormFieldLabel = { label: string; labelKey?: never } | { label?: never; labelKey: string };

type PosFormFieldBase = {
  name: string;
  placeholder: string;
  /** Defaults to true: a declared field is required unless said otherwise. */
  required?: boolean;
  /** Defaults to 'text'. A 'hidden' field is transient and derived: it only feeds conditions. */
  fieldType?: 'text' | 'select' | 'hidden';
  options?: { value: string; labelKey: string; helperTextKey?: string }[];
  /**
   * Drives the form but is never sent. Lets a form ask a question whose answer is already
   * implied by the stored data, instead of persisting a second source of truth.
   */
  isTransient?: boolean;
  /** Transient fields only: initial value read back from what is stored. */
  deriveValue?: (specificPosData: { [key: string]: unknown }) => string;
  /** Never rendered, always sent with this value: for what the form's answers already decide. */
  computeValue?: (form: PosFormValues) => string;
  /** Rendered, and sent, only while this holds (any of them, given a list). A hidden field is neither. */
  visibleWhen?: PosFieldCondition | PosFieldCondition[];
  /** Overrides `required` while it holds, so one mode can demand what another leaves free. */
  requiredWhen?: PosFieldCondition | PosFieldCondition[];
  helperTextKey?: string;
  /**
   * One predicate feeds both the Formsy rule that gates Save and the inline error, so the
   * two cannot disagree. Blank is always accepted — `required` alone decides emptiness.
   */
  isValueInvalid?: (value: string) => boolean;
  invalidMessageKey?: string;
};

export type PosFormField = PosFormFieldBase & PosFormFieldLabel;

/** An i18n key and its interpolation values, as the read-only recap renders them. */
export type PosResolvedTarget = { key: string; values: { [key: string]: string } };

export type PosFormList = {
  [key: string]: {
    label: string;
    fields: PosFormField[];
    /**
     * Help shown under the store id field, for a POS whose store id is not free text.
     */
    providerStoreIdHint?: string;
    /** The store id is a plain integer, so a composite pasted by hand is refused. */
    providerStoreIdIsNumeric?: boolean;
    /** Store ids the backend would refuse. Blank is left to `required`. */
    isProviderStoreIdInvalid?: (value: string) => boolean;
    providerStoreIdInvalidMessageKey?: string;
    /** Offered to LittleBill admins only; a merchant sees the account read-only. */
    adminOnly?: boolean;
    /**
     * The backend generates the store id when the account is created, and the till is set
     * up with it: left blank on creation, read-only afterwards.
     */
    providerStoreIdGenerated?: boolean;
    /** Sync, current-sale and click-and-collect are forced off by the backend: not offered. */
    hidesCapabilityFlags?: boolean;
    /**
     * Name of the specificPosData field the backend prefixes onto the store id to build
     * the value it matches sales on. Set it to show that value read-only.
     */
    providerStoreIdPrefixField?: string;
    /**
     * Read-only recap of what the backend derives when part of the connection is implicit.
     * Returns an i18n key and its values, or undefined while there is nothing to show.
     */
    resolvedTargetCaption?: (form: PosFormValues) => PosResolvedTarget | undefined;
    /** Read-only connection status, shown above the specific fields. */
    statusCaption?: (form: PosFormValues) => PosResolvedTarget | undefined;
    /**
     * specificPosData keys written by something other than this form (the POS itself, at
     * registration). Never rendered; sent back verbatim when stored, never as a blank.
     */
    preservedSpecificPosData?: string[];
  };
};

const conditionHolds = (condition: PosFieldCondition | PosFieldCondition[], form: PosFormValues): boolean =>
  (Array.isArray(condition) ? condition : [condition]).some(one => one.oneOf.includes(String(form[one.field] ?? '')));

export const isPosFieldVisible = (field: PosFormField, form: PosFormValues): boolean =>
  !field.visibleWhen || conditionHolds(field.visibleWhen, form);

export const isPosFieldRendered = (field: PosFormField, form: PosFormValues): boolean =>
  !field.computeValue && isPosFieldVisible(field, form);

export const isPosFieldRequired = (field: PosFormField, form: PosFormValues): boolean =>
  field.requiredWhen ? conditionHolds(field.requiredWhen, form) : (field.required ?? true);

/**
 * The "Pas encore connu" ticks that still apply to the form as it stands: the store id (unless
 * generated) and the rendered, required text fields. A tick left on a field the current mode hides
 * must neither null it nor flag the save as incomplete.
 */
export const applicableUnknownFields = (type: string, form: PosFormValues, unknownFields: string[]): string[] => {
  const config = posFormList[type];
  return unknownFields.filter(name => {
    if (name === 'providerStoreId') return !config?.providerStoreIdGenerated;
    const field = config?.fields.find(candidate => candidate.name === name);
    return Boolean(
      field &&
        field.fieldType !== 'hidden' &&
        field.fieldType !== 'select' &&
        isPosFieldRendered(field, form) &&
        isPosFieldRequired(field, form),
    );
  });
};

/** Whose Odoo API key the merchant gives. Only an administrator's lets LittleBill create the sales rule. */
export const ODOO_KEY_TYPE = {
  admin: 'admin',
  classic: 'classic',
} as const;

/** Which LittleBill artifact the merchant runs: the importable edition is the only one Odoo Online accepts. */
export const ODOO_EDITION = {
  online: 'online',
  module: 'module',
} as const;

/** Which of the two SumUp products the merchant runs. Stored, unlike the Odoo mode (see POSSU). */
export const SUMUP_PRODUCT = {
  posPro: 'pospro',
  register: 'register',
} as const;

/** The till whose connection settings are generated by LittleBill and shown once saved. */
export const KEZIA_POS_TYPE = 'POSKZ';
export const ETPOS_POS_TYPE = 'POSET';
/** What the ETPOS connection check can answer, `ERROR` being a check that did not complete. */
export const ETPOS_CONNECTION_MESSAGE_KEYS = {
  CONNECTED: 'dialogs.pos.etposConnectionConnected',
  INCOMPLETE: 'dialogs.pos.etposConnectionNeedsAccess',
  UNKNOWN_PROXY: 'dialogs.pos.etposConnectionUnknownProxy',
  RELAY_UNAVAILABLE: 'dialogs.pos.etposConnectionRelayUnavailable',
  TILL_UNREACHABLE: 'dialogs.pos.etposConnectionTillUnreachable',
  KEYS_REFUSED: 'dialogs.pos.etposConnectionKeysRefused',
  ERROR: 'dialogs.pos.etposConnectionError',
} as const;
/** The till numbers LittleBill declares to Kezia; a tag's posDeviceId must be one of them. */
export const KEZIA_TILLS = ['1', '2', '3'];

/** Whether the module has registered itself, read from the presence of its inbound secret. */
export const ODOO_REGISTRATION = {
  registered: 'registered',
  pending: 'pending',
} as const;

/** Whether the account already holds Odoo credentials from before the module registered itself. */
export const ODOO_STORED_CREDENTIALS = {
  present: 'yes',
  absent: 'no',
} as const;

/** Which Odoo hosting the merchant runs. Never stored — derived from what is (see POSDOO). */
export const ODOO_HOSTING_MODE = {
  online: 'odooOnline',
  sh: 'odooSh',
  selfHosted: 'selfHosted',
} as const;

// Looser than Formsy's isUrl on purpose: that rule needs a dotted host or literally
// "localhost", so it rejects http://odoo:8069, which the backend accepts towards a private
// network. Public-vs-private stays a server-side decision.
const HTTP_URL = /^https?:\/\/[^\s/:?#]+(:\d+)?(\/\S*)?$/i;
/** ETWS keys are shown in hexadecimal by ETPOS, an even number of digits. */
const HEX_KEY = /^(?:[0-9a-f]{2})+$/i;
export const isNotHexKey = (value: string): boolean => !HEX_KEY.test(value.trim());
/** The provider puts the id in the relay URL, so it refuses anything but a plain path segment. */
const ETPROXY_ID = /^[A-Za-z0-9_-]+$/;
export const isNotEtproxyId = (value: string): boolean => !ETPROXY_ID.test(value.trim());
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isNotGuid = (value: string): boolean => !GUID.test(value.trim());

export const EBP_POS_TYPE = 'POSEBP';

/** Display-only mirror of the backend's normalise_odoo_domain, enough to keep the recap honest. */
const displayOdooDomain = (value: string): string => {
  const host = value
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '');
  return host.endsWith('.odoo.com') ? host.split('.')[0] : host;
};

export const posFormList: PosFormList = {
  POSBOR: {
    label: 'Biborne',
    fields: [],
  },
  POSCLB: {
    label: 'Calabraise',
    fields: [],
  },
  POSC: {
    label: 'Carré POS',
    fields: [{ name: 'token', label: 'Token', placeholder: 'Token' }],
  },
  POSCPD: {
    label: 'Cashpad',
    fields: [
      { name: 'url', label: 'URL', placeholder: '<url-api-cashpad>', required: true },
      { name: 'email', label: 'Email', placeholder: 'merchant@example.com', required: true },
      { name: 'token', label: 'Token', placeholder: '<cashpad-api-token>', required: true },
    ],
  },
  HUB: {
    label: 'Chift',
    fields: [
      { name: 'chiftPosName', label: 'POS Name', placeholder: 'ZELTY' },
      { name: 'chiftAccountId', label: 'Account ID', placeholder: 'cdbaaa27-0e57-4841-9eda-3e0b8074d6bb' },
      { name: 'chiftClientId', label: 'Client ID', placeholder: 'p5ZkK5Fb0B3oNbC' },
      { name: 'chiftConsumerId', label: 'Consumer ID', placeholder: 'f5cf5e5c-98f4-4c8d-b8d5-00741de2d0ff' },
      { name: 'chiftClientSecret', label: 'Secret key', placeholder: 'OLybVMsnUZknJxaATWv5' },
      { name: 'chiftSignatureSecret', label: 'Signature secret', placeholder: 'knJxaATWv5OLybVMsnUZ' },
    ],
  },
  POSCLY: {
    label: 'Clyo',
    fields: [
      { name: 'account', label: 'Account', placeholder: 'identifiant magasin LittleBill', required: true },
      { name: 'baseUrl', label: 'URL WebApi', placeholder: 'http://192.168.1.10:9000', required: false },
    ],
  },
  POSCRI: {
    label: 'Crisalid',
    fields: [],
  },
  [EBP_POS_TYPE]: {
    label: 'EBP',
    adminOnly: true,
    // Read at scan time by the local connector: no sync, no current sale, no click and collect.
    hidesCapabilityFlags: true,
    providerStoreIdHint: 'dialogs.pos.ebpStorehouseIdHint',
    isProviderStoreIdInvalid: isNotGuid,
    providerStoreIdInvalidMessageKey: 'dialogs.pos.ebpStorehouseIdInvalid',
    // Picked in EbpConnectorSection, from the franchise's connectors.
    fields: [{ name: 'connectorId', labelKey: 'dialogs.pos.ebpConnector', placeholder: '', fieldType: 'hidden' }],
  },
  POSET: {
    label: 'ET POS',
    // All optional: a till without ETPROXY access keeps sending its sales by webhook.
    fields: [
      {
        name: 'proxyId',
        labelKey: 'dialogs.pos.etposProxyId',
        placeholder: 'etws-0123456789abcdef012345',
        required: false,
        isValueInvalid: isNotEtproxyId,
        invalidMessageKey: 'dialogs.pos.etposProxyIdInvalid',
        helperTextKey: 'dialogs.pos.etposProxyIdHelp',
      },
      {
        name: 'publicKey',
        labelKey: 'dialogs.pos.etposPublicKey',
        placeholder: 'BF98',
        required: false,
        isValueInvalid: isNotHexKey,
        invalidMessageKey: 'dialogs.pos.etposKeyInvalid',
        helperTextKey: 'dialogs.pos.etposKeysHelp',
      },
      {
        name: 'privateKey',
        labelKey: 'dialogs.pos.etposPrivateKey',
        placeholder: '89BFC743CA',
        required: false,
        isValueInvalid: isNotHexKey,
        invalidMessageKey: 'dialogs.pos.etposKeyInvalid',
      },
    ],
  },
  POSFA: {
    label: 'Fülle',
    fields: [
      { name: 'token', label: 'Token', placeholder: '<fulle-api-token>' },
      { name: 'inputValue', label: 'Input value', placeholder: '1' },
      { name: 'outputValue', label: 'Output value', placeholder: '1' },
    ],
  },
  POSHB: {
    label: 'Hiboutik',
    fields: [
      { name: 'url', label: 'URL', placeholder: 'https://your-shop.hiboutik.com' },
      { name: 'account', label: 'Account', placeholder: 'your-account' },
      { name: 'email', label: 'Email', placeholder: 'merchant@example.com' },
      { name: 'token', label: 'Token', placeholder: '<hiboutik-api-token>' },
    ],
  },
  POSKZ: {
    label: 'Kezia II (JDC)',
    providerStoreIdHint: 'dialogs.pos.providerStoreIdHintKezia',
    providerStoreIdGenerated: true,
    hidesCapabilityFlags: true,
    fields: [],
  },
  POSKWZ: {
    label: 'Kwisatz',
    fields: [],
  },
  POSADD: {
    label: "L'addition",
    fields: [],
  },
  POSLEO: {
    label: 'LEO2',
    providerStoreIdHint: 'dialogs.pos.leo2StoreIdHelp',
    fields: [
      {
        name: 'token',
        labelKey: 'dialogs.pos.leo2Token',
        placeholder: 'LEO2STATS',
        required: true,
        helperTextKey: 'dialogs.pos.leo2TokenHelp',
      },
    ],
  },
  POSMNY: {
    label: 'Money',
    // Money sales are matched on the VAT number carried by each receipt.
    providerStoreIdHint: 'dialogs.pos.providerStoreIdHintMoney',
    fields: [],
  },
  POSNXT: {
    label: 'Nextore',
    fields: [],
  },
  POSDOO: {
    label: 'Odoo',
    fields: [
      {
        name: 'odooRegistration',
        label: '',
        placeholder: '',
        fieldType: 'hidden',
        isTransient: true,
        required: false,
        deriveValue: specificPosData =>
          specificPosData.inboundSecret ? ODOO_REGISTRATION.registered : ODOO_REGISTRATION.pending,
      },
      {
        // Credentials the module edition no longer needs stay on screen where an older
        // account holds them, so they can still be read, changed or cleared.
        name: 'odooHasStoredCredentials',
        label: '',
        placeholder: '',
        fieldType: 'hidden',
        isTransient: true,
        required: false,
        deriveValue: specificPosData =>
          specificPosData.username || specificPosData.apiKey
            ? ODOO_STORED_CREDENTIALS.present
            : ODOO_STORED_CREDENTIALS.absent,
      },
      {
        name: 'odooHostingMode',
        labelKey: 'dialogs.pos.odooHosting',
        placeholder: '',
        fieldType: 'select',
        isTransient: true,
        options: [
          {
            value: ODOO_HOSTING_MODE.online,
            labelKey: 'dialogs.pos.odooHostingOnline',
            helperTextKey: 'dialogs.pos.odooHostingOnlineHelp',
          },
          {
            value: ODOO_HOSTING_MODE.sh,
            labelKey: 'dialogs.pos.odooHostingSh',
            helperTextKey: 'dialogs.pos.odooHostingShHelp',
          },
          {
            value: ODOO_HOSTING_MODE.selfHosted,
            labelKey: 'dialogs.pos.odooHostingSelfHosted',
            helperTextKey: 'dialogs.pos.odooHostingSelfHostedHelp',
          },
        ],
        // Read back the way the backend reads it, so nothing has to be stored: a base URL
        // means self-hosted, a database alone means odoo.sh, neither means Odoo Online.
        // So an odoo.sh account given a base URL re-opens as self-hosted. Cosmetic: the
        // mode only picks which fields show, and the recap below still names the target.
        deriveValue: specificPosData => {
          if (specificPosData.baseUrl) return ODOO_HOSTING_MODE.selfHosted;
          if (specificPosData.database) return ODOO_HOSTING_MODE.sh;
          // A module account awaiting its registration has neither address nor login yet.
          // An Online account always has a login, even one stored with the default edition.
          const awaitsRegistration =
            specificPosData.edition === ODOO_EDITION.module && !specificPosData.username && !specificPosData.apiKey;
          return awaitsRegistration ? ODOO_HOSTING_MODE.selfHosted : ODOO_HOSTING_MODE.online;
        },
      },
      {
        name: 'domain',
        label: 'Nom du domaine',
        placeholder: 'acme, ou odoo.acme.fr',
        required: true,
      },
      {
        name: 'username',
        labelKey: 'dialogs.pos.odooUsername',
        placeholder: 'user@mail.com',
        required: false,
        // Only the Online edition logs in; littlebill_pos registers itself with no Odoo user.
        requiredWhen: { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.online] },
        visibleWhen: [
          { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.online] },
          { field: 'odooHasStoredCredentials', oneOf: [ODOO_STORED_CREDENTIALS.present] },
        ],
        helperTextKey: 'dialogs.pos.odooCredentialsHelp',
      },
      {
        name: 'apiKey',
        labelKey: 'dialogs.pos.odooApiKey',
        placeholder: 'hsIefQDmAGf4EX9wPi336ldPVWj99B7u==',
        required: false,
        requiredWhen: { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.online] },
        visibleWhen: [
          { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.online] },
          { field: 'odooHasStoredCredentials', oneOf: [ODOO_STORED_CREDENTIALS.present] },
        ],
      },
      {
        name: 'keyType',
        labelKey: 'dialogs.pos.odooKeyType',
        placeholder: '',
        fieldType: 'select',
        options: [
          {
            value: ODOO_KEY_TYPE.admin,
            labelKey: 'dialogs.pos.odooKeyTypeAdmin',
            helperTextKey: 'dialogs.pos.odooKeyTypeAdminHelp',
          },
          {
            value: ODOO_KEY_TYPE.classic,
            labelKey: 'dialogs.pos.odooKeyTypeClassic',
            helperTextKey: 'dialogs.pos.odooKeyTypeClassicHelp',
          },
        ],
        // Hidden means not sent, so the other hostings keep the backend's classic default.
        visibleWhen: { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.online] },
        // Accounts saved before this field existed have none, and the backend reads that as classic.
        deriveValue: specificPosData =>
          typeof specificPosData.keyType === 'string' && specificPosData.keyType
            ? specificPosData.keyType
            : ODOO_KEY_TYPE.classic,
      },
      {
        name: 'edition',
        label: 'Édition LittleBill',
        placeholder: '',
        // Odoo Online can only import littlebill_pos_online; the module edition needs odoo.sh
        // or a self-hosted instance. Without it the backend read every account as the module.
        computeValue: form =>
          form.odooHostingMode === ODOO_HOSTING_MODE.online ? ODOO_EDITION.online : ODOO_EDITION.module,
      },
      // Absent from this list, these two were wiped on every save: the payload is
      // rebuilt from the fields declared here.
      {
        name: 'baseUrl',
        label: "URL de l'instance Odoo",
        placeholder: 'https://odoo.acme.fr',
        // Never required: littlebill_pos 1.2+ registers its own address when the shops are
        // refreshed. Still editable for accounts that predate the registration.
        required: false,
        visibleWhen: { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.sh, ODOO_HOSTING_MODE.selfHosted] },
        isValueInvalid: value => !HTTP_URL.test(value.trim()),
        invalidMessageKey: 'dialogs.pos.baseUrlInvalid',
      },
      {
        name: 'database',
        label: 'Base de données',
        placeholder: 'acme-master-1234567',
        // Never required, like the URL: the module registers its database name itself.
        required: false,
        visibleWhen: { field: 'odooHostingMode', oneOf: [ODOO_HOSTING_MODE.sh, ODOO_HOSTING_MODE.selfHosted] },
      },
    ],
    providerStoreIdHint: 'dialogs.pos.providerStoreIdHintOdoo',
    providerStoreIdIsNumeric: true,
    providerStoreIdPrefixField: 'domain',
    preservedSpecificPosData: ['inboundSecret'],
    statusCaption: (form): PosResolvedTarget | undefined => {
      if (form.odooHostingMode === ODOO_HOSTING_MODE.online) return undefined;
      return {
        key:
          form.odooRegistration === ODOO_REGISTRATION.registered
            ? 'dialogs.pos.odooModuleConnected'
            : 'dialogs.pos.odooModulePending',
        values: {},
      };
    },
    // Mirrors get_odoo_base_url / get_odoo_database in lb-provider-integration.
    resolvedTargetCaption: (form): PosResolvedTarget | undefined => {
      const domain = displayOdooDomain(String(form.domain || ''));
      if (!domain) return undefined;
      const baseUrl = String(form.baseUrl || '').trim();
      const database = String(form.database || '').trim();
      // The `.odoo.com` fallback only fits a single-label identifier; suffixing it onto a
      // hostname names a host that cannot resolve, so ask rather than recite the guess.
      if (!baseUrl && domain.includes('.')) {
        return { key: 'dialogs.pos.odooResolvedTargetNeedsUrl', values: { domain } };
      }
      return {
        key: 'dialogs.pos.odooResolvedTarget',
        values: { url: baseUrl || `https://${domain}.odoo.com`, database: database || domain },
      };
    },
  },
  POSPOP: {
    label: 'Popina / JDC',
    fields: [],
  },
  POSPSS: {
    label: 'Prestashop',
    fields: [],
  },
  POSPRS: {
    label: 'Proshop',
    fields: [],
  },
  POSCSH: {
    label: 'S2Cash',
    fields: [{ name: 'authToken', label: 'AuthToken', placeholder: '', required: true }],
  },
  POSSCA: {
    label: 'Shop Caisse',
    fields: [
      {
        name: 'token',
        label: 'Token',
        placeholder: '',
        required: true,
      },
      {
        name: 'applicationId',
        label: "UUID d'application (clé de signature)",
        placeholder: '0b4c2f1e-7a63-4d18-9f52-6c8e3a1d5b74',
      },
    ],
  },
  POSSQR: {
    label: 'Square',
    fields: [{ name: 'token', label: 'Access Token', placeholder: 'EAAAEJ...mQZDZD', required: true }],
  },
  POSSU: {
    label: 'SumUp',
    providerStoreIdHint: 'dialogs.pos.providerStoreIdHintSumup',
    fields: [
      {
        name: 'mode',
        labelKey: 'dialogs.pos.sumupProduct',
        placeholder: '',
        fieldType: 'select',
        options: [
          {
            value: SUMUP_PRODUCT.posPro,
            labelKey: 'dialogs.pos.sumupProductPosPro',
            helperTextKey: 'dialogs.pos.sumupProductPosProHelp',
          },
          {
            value: SUMUP_PRODUCT.register,
            labelKey: 'dialogs.pos.sumupProductRegister',
            helperTextKey: 'dialogs.pos.sumupProductRegisterHelp',
          },
        ],
        // Stored, unlike Odoo's hosting mode, but an account configured before this field
        // existed has none: the backend reads that as POS Pro, so the form must show the
        // same thing rather than an empty select.
        deriveValue: specificPosData => (specificPosData.mode as string) || SUMUP_PRODUCT.posPro,
      },
      {
        name: 'apiKey',
        labelKey: 'dialogs.pos.sumupApiKey',
        placeholder: 'sup_sk_...',
        required: false,
        requiredWhen: { field: 'mode', oneOf: [SUMUP_PRODUCT.register] },
        visibleWhen: { field: 'mode', oneOf: [SUMUP_PRODUCT.register] },
      },
    ],
  },
  POSTO: {
    label: 'Toporder',
    fields: [],
  },
  POSWOC: {
    label: 'WooCommerce',
    fields: [],
  },
  POSXL: {
    label: 'XL Pos',
    fields: [],
  },
  POSZL: {
    label: 'Zelty',
    fields: [
      {
        name: 'apiKey',
        label: 'API key',
        placeholder: 'hsIefQDmAGf4EX9wPi336ldPVWj99B7u==',
        required: true,
      },
    ],
  },
};

/** A till can be configured by a script before the dashboard knows its type (EBP, at first). */
export const isKnownPosType = (type: string): boolean => Object.prototype.hasOwnProperty.call(posFormList, type);

/** Whether this user may edit an account of this type from the dashboard. */
export const isConfigurablePosType = (type: string, isAdmin: boolean): boolean =>
  isKnownPosType(type) && (isAdmin || !posFormList[type].adminOnly);

/** A till the dashboard cannot configure still shows its raw type rather than an empty name. */
export const posDisplayName = (type: string): string => (isKnownPosType(type) ? posFormList[type].label : type);

/** The POS list as offered to the merchant: by label, whatever order the keys were declared in. */
export const sortedPosEntries = (list: PosFormList = posFormList): [string, PosFormList[string]][] =>
  Object.entries(list).sort(([, a], [, b]) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));

/**
 * The form after `name` takes `value`. A field this change hides keeps its old value in
 * state: a transient one goes back to what the stored data implies, since its selector can
 * no longer be reached, then a stored one is blanked so the recap, the payload and the mode
 * derived on the next load all agree.
 */
export const applyPosFormChange = (
  type: string,
  form: PosFormValues,
  name: string,
  value: string,
  stored: { [key: string]: unknown },
): PosFormValues => {
  const fields = posFormList[type]?.fields ?? [];
  const changed: PosFormValues = { ...form, [name]: value };
  const transientReset = fields
    .filter(field => field.isTransient && field.deriveValue)
    .filter(field => isPosFieldVisible(field, form) && !isPosFieldVisible(field, changed))
    .reduce<PosFormValues>(
      (next, field) => ({ ...next, [field.name]: field.deriveValue ? field.deriveValue(stored) : '' }),
      changed,
    );
  return fields
    .filter(field => !field.isTransient)
    .filter(field => isPosFieldVisible(field, form) && !isPosFieldVisible(field, transientReset))
    .reduce<PosFormValues>((next, field) => ({ ...next, [field.name]: '' }), transientReset);
};

/**
 * The specificPosData a save sends. `stored` is what the account holds under this same POS
 * type, so preserved keys survive a save the form itself cannot express.
 */
/**
 * The store id the form starts from when the POS type changes. A generated id belongs to
 * the account that generated it: it never seeds another POS, and no other POS's id seeds it
 * (the backend keeps a non-blank id as the client id the till is configured with).
 */
export const providerStoreIdOnSwitch = (
  stored: { type: string; providerStoreId?: string | null } | null | undefined,
  nextType: string,
): string => {
  if (!stored) return '';
  if (stored.type === nextType) return stored.providerStoreId || '';
  const involvesGeneratedId =
    Boolean(posFormList[nextType]?.providerStoreIdGenerated) ||
    Boolean(posFormList[stored.type]?.providerStoreIdGenerated);
  return involvesGeneratedId ? '' : stored.providerStoreId || '';
};

export const buildSpecificPosData = (
  type: string,
  form: PosFormValues,
  stored: { [key: string]: unknown },
  unknownFields: string[] = [],
): { [key: string]: unknown } => {
  const config = posFormList[type];
  // A blank optional field is omitted rather than sent as "": the backend treats both
  // as unset, and storing "" makes an untouched account look deliberately configured.
  // A field the current mode hides is omitted too, which is how switching mode clears it.
  const declared = (config?.fields ?? [])
    .filter(field => !field.isTransient && isPosFieldVisible(field, form))
    // "Pas encore connu": left out entirely, so the backend records it as missing.
    .filter(field => !unknownFields.includes(field.name))
    .map(field => {
      const value = field.computeValue ? field.computeValue(form) : form[field.name];
      return { field, value: typeof value === 'string' ? value.trim() : value };
    })
    .filter(({ field, value }) => value || isPosFieldRequired(field, form))
    .map(({ field, value }) => [field.name, value]);
  const preserved = (config?.preservedSpecificPosData ?? [])
    .filter(name => stored[name])
    .map(name => [name, stored[name]]);
  return Object.fromEntries([...declared, ...preserved]);
};

/** Label of a setup field the backend reported missing, as the form shows it. */
export const missingFieldLabel = (type: string, key: string): string => {
  if (key === 'providerStoreId') return i18n.t('dialogs.pos.providerStoreId');
  const field = posFormList[type]?.fields.find(candidate => candidate.name === key);
  if (!field) return key;
  return field.labelKey ? i18n.t(field.labelKey) : (field.label as string);
};

const PLACEHOLDER_WORDS = ['test', 'xxx', 'todo', 'tbd', 'à remplir', 'a remplir', 'exemple'];

// Whole value made of consecutive ascending digits, at least 3 of them (123, 2345).
const isAscendingDigits = (value: string): boolean =>
  /^\d{3,}$/.test(value) &&
  value.split('').every((digit, index) => index === 0 || Number(digit) === Number(value[index - 1]) + 1);

/** Same rule as salesapp services/placeholder.ts and the lb-scripts report: keep the three identical. */
export const looksLikePlaceholder = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;
  return /^(.)\1*$/su.test(normalized) || isAscendingDigits(normalized) || PLACEHOLDER_WORDS.includes(normalized);
};

export interface PosForm {
  type: string;
  providerStoreId: string;
  canSync: boolean;
  canManageCurrentSale: boolean;
  canRedeemLoyaltyOffer: boolean;
  hasClickAndCollect: boolean;
  resetDelay: number;
  linkReceiptDelay: number;
  [key: string]: string | boolean | number;
}

const CARRE_POS_TYPE = 'POSC';

/** CarrePOS Menu Online loyalty starts on for a new account; an existing account keeps what it stores. */
export const initialHasClickAndCollect = (
  type: string,
  stored: boolean | null | undefined,
  isNewAccount: boolean,
): boolean => stored ?? (isNewAccount && type === CARRE_POS_TYPE);

export const defaultPosFormState: PosForm = {
  type: '',
  providerStoreId: '',
  canSync: false,
  canManageCurrentSale: false,
  canRedeemLoyaltyOffer: true,
  hasClickAndCollect: false,
  resetDelay: 120,
  linkReceiptDelay: 120,
};
